/**
 * Espejo de las respuestas de `/metas-colaborador` en la API
 * (`cdr-sa-api/src/modules/metas-colaborador/application/metas.types.ts` y el
 * caso de uso). Si allá cambia una forma, cambia aquí.
 *
 * ⚠️ Las fechas SUELTAS (`lunes`, `domingo`, `fechaAlta`, `hoy`) llegan como
 * texto `AAAA-MM-DD` y así se quedan: nunca pasan por `new Date()`, que en el
 * navegador las corre al día anterior según la hora. Los INSTANTES
 * (`enviadoEn`, `resueltoEn`, `actualizadoEn`) llegan como marca de tiempo ISO
 * completa y sólo se formatean para mostrarlos.
 */

/**
 * `direccion` (21-oct-2026, decisión del usuario: «un rol más directivo con
 * información importante»): ve TODAS las tiendas en sólo lectura. No captura,
 * no autoriza, no reabre ni ve Accesos. Su nombre es fijo («TODAS»).
 */
export type TipoAlcance = 'supervisor' | 'regional' | 'direccion';

/** `null` = la cuenta no tiene tiendas asignadas: el menú se esconde. */
export type TipoMiAlcance = 'admin' | 'supervisor' | 'regional' | 'direccion' | 'mixto' | null;

/**
 * `revision` (21-oct-2026, decisión del usuario, fase B): «con el supervisor».
 * El gerente captura desde la app con la cuenta de la tienda y se la manda a su
 * supervisor, que la ajusta, se la devuelve (vuelve a borrador, con comentario)
 * o la envía al regional. El supervisor sigue pudiendo capturar él mismo una
 * tienda en borrador y enviarla directo.
 */
export type EstadoTienda =
  | 'sin_captura'
  | 'borrador'
  | 'revision'
  | 'enviada'
  | 'autorizada'
  | 'rechazada';

export const ESTADOS_TIENDA: EstadoTienda[] = [
  'sin_captura',
  'borrador',
  'revision',
  'enviada',
  'autorizada',
  'rechazada',
];

/** Los que se pueden elegir hoy (decisión del 29-sep). */
export type MotivoCapturable =
  | 'vacaciones'
  | 'incapacidad'
  | 'capacitacion'
  | 'apoyo_otra_tienda'
  | 'baja_en_proceso'
  | 'tienda_erronea'
  | 'otro';

/**
 * Guardados antes del 29-sep: la base los acepta, la captura no. Ahora quién
 * lleva meta lo dice el PUESTO de BUK (`ReglaMeta`). «Baja» ya no existe: la
 * migración la pasó a «Baja en proceso».
 */
export type MotivoHistorico = 'cajero' | 'gerente';

/** Lo que puede haber guardado en una fila. */
export type MotivoSinMeta = MotivoCapturable | MotivoHistorico;

/** La misma lista cerrada que `MOTIVOS_SIN_META` de `domain/motivos.ts` de la API. */
export const MOTIVOS_SIN_META: MotivoCapturable[] = [
  'vacaciones',
  'incapacidad',
  'capacitacion',
  'apoyo_otra_tienda',
  'baja_en_proceso',
  'tienda_erronea',
  'otro',
];
export const MOTIVOS_CAPTURABLES = MOTIVOS_SIN_META;

export const MOTIVOS_HISTORICOS: MotivoHistorico[] = ['cajero', 'gerente'];

/** Etiquetas de respaldo; las que manda `mi-alcance` mandan sobre éstas. */
export const ETIQUETA_MOTIVO: Record<MotivoSinMeta, string> = {
  vacaciones: 'Vacaciones',
  incapacidad: 'Incapacidad',
  capacitacion: 'Capacitación',
  apoyo_otra_tienda: 'Apoyo en otra tienda',
  baja_en_proceso: 'Baja en proceso',
  tienda_erronea: 'Tienda errónea',
  otro: 'Otro',
  cajero: 'Cajero/a',
  gerente: 'Gerente',
};

export const esMotivoHistorico = (v: unknown): v is MotivoHistorico =>
  typeof v === 'string' && (MOTIVOS_HISTORICOS as string[]).includes(v);

/**
 * Qué dice el puesto de BUK sobre la meta individual:
 * - `obligatoria`: sin meta ni motivo, la fila está PENDIENTE.
 * - `opcional`: puede llevar meta o nada (gerentes).
 * - `no_lleva`: la casilla de la META es de sólo lectura (cajeros, bodegueros); motivo y apoyo sí se capturan.
 *   Salvo un puesto de bodega (`esBodega`, 23-oct-2026): su meta es OPCIONAL y, si
 *   la lleva, sale «Se le está colocando meta al bodeguero» (`utils/bodeguero.ts`).
 */
export type ReglaMeta = 'obligatoria' | 'opcional' | 'no_lleva';

export interface ReglaPuesto {
  patron: string;
  meta: ReglaMeta;
  esquema: string | null;
}

/**
 * - `posterior`: entró DESPUÉS de terminada la semana.
 * - `ingreso`: entró esa semana de jueves a domingo; la semana no cuenta.
 * - `induccion`, `garantia` y `vendedor`: sus semanas las dice la API en
 *   `semanasEtapa`, con la regla de esa semana. Hasta la S44 de 2026, 1-3, 4-12 y
 *   13+; desde la S45 ya no hay garantía (21-oct-2026, decisión del usuario).
 */
export type Etapa = 'posterior' | 'ingreso' | 'induccion' | 'garantia' | 'vendedor';

export type Base = 'CADER' | 'TRENDER';

export type NivelAviso = 'error' | 'advertencia' | 'info';

export interface Aviso {
  codigo: string;
  nivel: NivelAviso;
  key?: string;
  mensaje: string;
  /** Sólo en `PENDIENTE_CATALOGO` (422 al enviar o autorizar): el tipo de pendiente que lo impide. */
  tipo?: string;
  referencia?: string;
  detalle?: string;
  area?: string;
}

/** El `codigo` de un 422 de enviar/autorizar por un pendiente de catálogo que bloquea (fase 4). */
export const CODIGO_PENDIENTE_CATALOGO = 'PENDIENTE_CATALOGO';

export interface ParametrosMetas {
  metaMinima: number;
  toleranciaSuma: number;
  factorMax: number;
  /** Meta más de este porcentaje debajo de la venta promedio: advertencia. */
  umbralBajoPromedio: number;
  diasApoyoMin: number;
  semanasAtrasCaptura: number;
  semanasAdelanteCaptura: number;
  /** Día ISO (lunes = 1) hasta el que se resuelve la semana recién cerrada. */
  diaLimiteResolverAnterior: number;
  semanasPromedio: number;
  semanasPromedioMin: number;
  /** Largo mínimo del comentario al rechazar o reabrir. */
  comentarioMin: number;
}

/** Mínimo de caracteres del comentario si la API no lo manda. */
export const COMENTARIO_MIN_POR_OMISION = 10;

export interface TiendaAlcance {
  chain: string;
  num: number;
  tienda: string;
  supervisor: string;
  regional: string;
}

/** Una tienda del catálogo de la API (`STORE_CATALOG`): cadena + número + nombre. */
export interface TiendaCatalogo {
  chain: string;
  num: number;
  tienda: string;
}

export interface OpcionMotivo {
  valor: MotivoSinMeta;
  etiqueta: string;
}

/** `GET /metas-colaborador/mi-alcance` */
export interface MiAlcance {
  tipo: TipoMiAlcance;
  esAdmin: boolean;
  /**
   * Tiene el alcance de dirección (21-oct-2026): ve todas las tiendas y no
   * escribe. Aparte de `tipo`, que con otro alcance encima dice «mixto».
   * Opcional: una API anterior no lo manda (se deduce de `alcances`).
   */
  esDireccion?: boolean;
  alcances: { tipo: TipoAlcance; nombre: string }[];
  puedeCapturar: boolean;
  puedeAutorizar: boolean;
  /** Hoy en hora de México, calculado por el servidor. */
  hoy: string;
  semanaActual: string;
  /** @deprecated alias de `ventanaCaptura`. */
  ventana: string[];
  /** Semanas que un supervisor captura (por omisión, la actual y la siguiente). */
  ventanaCaptura?: string[];
  /** Semanas que un regional resuelve: además, la recién cerrada hasta el día límite. */
  ventanaResolucion?: string[];
  tiendas: TiendaAlcance[];
  /** Los motivos que se pueden ELEGIR, con su etiqueta. */
  motivos: OpcionMotivo[];
  /** Sólo para mostrar lo guardado antes del 29-sep. */
  motivosHistoricos?: OpcionMotivo[];
  /**
   * El catálogo EXACTO con que la API valida un apoyo (158 tiendas). La lista
   * del diálogo de apoyo sale de aquí y NO de `/dashboard/catalogo-tiendas`,
   * que es del ERP: cambia de madrugada —ADUANA desaparece— y trae tiendas
   * que la API rechaza con 400 al guardar.
   */
  catalogo: TiendaCatalogo[];
  parametros: ParametrosMetas;
  /** Quién lleva meta individual según su puesto; gana la primera que coincide. */
  reglasPuesto?: ReglaPuesto[];
  /**
   * La tienda captura su meta desde la app (el gerente, fase B) y se la manda
   * al supervisor. Interruptor POR ENTORNO que la API lee de la base
   * (`config.metasColaborador.parametros.capturaTienda`), nunca una constante
   * compilada (lección del 31-ago: el interruptor que apagó los dos entornos).
   * Decisión del usuario, 22-oct-2026: «de momento seguir dejando la tarea de
   * capturar la meta a los supervisores»; por omisión APAGADO. Una API anterior
   * no lo manda, y entonces también es `false` (`capturaDeTienda`).
   */
  capturaTienda?: boolean;
}

// ------------------------------------------------------------ alcances (admin)

export interface AlcanceAsignadoVista {
  id: number;
  userId: string;
  email: string;
  nombreUsuario: string | null;
  rol: string;
  /** La cuenta se eliminó pero el alcance sigue vigente. */
  cuentaEliminada: boolean;
  tipo: TipoAlcance;
  nombre: string;
  /** `false` = el nombre ya no existe en el catálogo: esa cuenta no ve ninguna tienda. */
  nombreEnCatalogo: boolean;
  asignadoPor: string;
  asignadoEn: string;
}

export interface CuentaAsignable {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

export interface OpcionNombre {
  nombre: string;
  tiendas: number;
}

/** `GET /metas-colaborador/accesos` */
export interface ListaAlcances {
  alcances: AlcanceAsignadoVista[];
  cuentas: CuentaAsignable[];
  opciones: { supervisores: OpcionNombre[]; regionales: OpcionNombre[] };
  sinCuenta: { supervisores: string[]; regionales: string[] };
}

export interface AsignarAlcanceRequest {
  userId: string;
  tipo: TipoAlcance;
  /** Del catálogo, para supervisor y regional. Dirección no lo lleva (la API guarda «TODAS»). */
  nombre?: string;
}

export interface AlcanceAsignadoRespuesta {
  id: number;
  email: string;
  tipo: TipoAlcance;
  nombre: string;
}

// ------------------------------------------------------------ semana

export interface TotalesCaptura {
  total: number;
  conMeta: number;
  sinMeta: number;
  /** Sin meta ni motivo porque su puesto no la obliga (cajeros, bodegueros, gerentes). */
  sinMetaPorPuesto?: number;
  pendientes: number;
  /**
   * Por la regla del puesto, tengan meta o no (21-oct-2026). «Colaboradores
   * listos» es «de las `obligatorias`, cuántas tienen meta o motivo»; las
   * opcionales (gerente, subgerente) y las que no la llevan van aparte. Sólo
   * los calcula el navegador (`totalesCaptura`): la API no los manda.
   */
  obligatorias?: number;
  opcionales?: number;
  noLleva?: number;
  suma: number;
  /**
   * La meta de la Casa (23-oct-2026, ronda F): va en `suma` —«su meta cuenta
   * para cuadrar la meta de la tienda»— pero la Casa no es persona y no entra en
   * `total` ni en ningún conteo. Ausente = sin Casa o sin meta.
   */
  metaCasa?: number;
  metaTienda: number | null;
  diferencia: number | null;
  diferenciaPct: number | null;
}

/** Los 3 chequeos del panel del regional (`revisionAutomatica` de la API). */
export interface RevisionAutomatica {
  /**
   * La API cuenta a todas las filas. El panel la rehace con las filas de la
   * vista (`coberturaDeFilas`): sólo las obligatorias, y el desglose por
   * puesto en `sinMetaPorPuesto` (no la llevan) y `opcionales`.
   */
  cobertura: { listos: number; total: number; porMotivo: Record<string, number>; sinMetaPorPuesto?: number; opcionales?: number };
  bajoPromedio: { key: string; nombre: string; meta: number; promedio: number; pct: number }[];
  suma: { metaTienda: number; suma: number; diferencia: number; pct: number; fuera: boolean } | null;
  alertas: number;
  /**
   * Bodegueros con meta esta semana (23-oct-2026): «Se le está colocando meta al
   * bodeguero». Sólo informa: la API NO lo cuenta en `alertas` (la tienda sigue
   * en el lote). Ausente = ninguno (o un resumen de antes del cambio).
   */
  bodeguerosConMeta?: { key: string; nombre: string; meta: number; puesto: string | null }[];
  /**
   * La Casa con meta esta semana (23-oct-2026, ronda F): «Se le está colocando
   * meta a la Casa». Como la del bodeguero, sólo informa (fuera de `alertas`).
   * Ausente = sin Casa, sin meta o una API anterior.
   */
  casaConMeta?: { key: string; nombre: string; meta: number };
}

export interface ResumenTienda extends TotalesCaptura {
  errores: number;
  advertencias: number;
  revision?: RevisionAutomatica;
}

export interface MetaTiendaVista {
  monto: number | null;
  /** Días de la semana con meta cargada. */
  dias: number;
  /** Sólo con los 7 días SIN IVA: una suma de 5 días no es la meta de la semana. */
  completa: boolean;
  /** Algún día llegó CON IVA: no se convierte y la semana no está completa. */
  conIva?: boolean;
}

/**
 * Excel «para comisiones» de una semana (decisión del 30-sep, «que venza los
 * martes»): no se baja mientras la semana aún se pueda autorizar o rechazar,
 * para nadie (el admin incluido). `bloqueo` es el mismo texto del 409 de la API
 * («Disponible a partir del miércoles …»); `null` = ya se puede bajar.
 */
export interface ComisionesDisponibilidad {
  /** Primer día (hora de México) en que se puede bajar. */
  disponibleDesde: string;
  bloqueo: string | null;
}

export interface SemanaInfo {
  lunes: string;
  domingo: string;
  numero: number;
  editable: boolean;
  /** La cuenta puede autorizar o rechazar esa semana (sólo en la lista de tiendas). */
  resoluble?: boolean;
  /** `autorizadas.xlsx` (sólo en la lista de tiendas). Opcional: una API anterior no lo manda. */
  comisiones?: ComisionesDisponibilidad;
}

/** Una fila de `GET semanas/:lunes/tiendas`. */
export interface TiendaSemana {
  chain: string;
  num: number;
  tienda: string;
  supervisor: string;
  regional: string;
  estado: EstadoTienda;
  version: number;
  /** Cuántas veces se envió (2 = «2º envío»). */
  numEnvio?: number;
  /**
   * Lo que se guardó con el último guardado o envío. `null` si la tienda nunca
   * se guardó. ⚠️ Puede venir incompleto (la API guarda `{}` si resuelve una
   * tienda sin resumen): leer cada campo con respaldo.
   */
  resumen: Partial<ResumenTienda> | null;
  metaTienda: MetaTiendaVista;
  enviadoEn: string | null;
  /** Correo de quien envió; el nombre va en `enviadoPorNombre`. */
  enviadoPor: string | null;
  enviadoPorNombre?: string | null;
  resueltoEn: string | null;
  resueltoPor: string | null;
  resueltoPorNombre?: string | null;
  comentario: string | null;
  actualizadoEn: string | null;
  /** `false` en enviada y autorizada: son de sólo lectura. */
  puedeCapturar: boolean;
  /**
   * Va en el selector de la Captura (lo decide la API): la cuenta captura la
   * tienda y la semana es capturable para ella —en cualquier estado, las
   * enviadas y autorizadas de sólo lectura—, o la tienda ya se envió y la
   * semana está en `ventanaResolucion` (el plazo para corregir un rechazo).
   * Opcional sólo por tolerancia: sin él, `puedeCapturar`.
   */
  verEnCaptura?: boolean;
  puedeAutorizar: boolean;
  /** Sólo una tienda ENVIADA se rechaza (una autorizada ya no). */
  puedeRechazar: boolean;
  /** Sólo administradores, sobre una tienda autorizada. */
  puedeReabrir?: boolean;
  /**
   * Se ofrece en «Autorizar seleccionadas»: `puedeAutorizar` y SIN alertas
   * conocidas (`alertas === 0`). Una con alertas se autoriza desde el detalle.
   */
  puedeAutorizarEnLote?: boolean;
  /**
   * Por qué una tienda SIN alertas no va en el lote: la fuente ya cargó la semana
   * para la cadena y la meta de esta tienda está incompleta, con IVA o en $0, así
   * que la suma no se pudo comparar. `null`/ausente: nada lo impide.
   */
  fueraDeLote?: string | null;
  /**
   * La revisión automática guardada con el último guardado o envío. `null` = el
   * resumen es de antes de que existiera (se completa al abrir el detalle).
   */
  revision?: RevisionAutomatica | null;
  /** `revision.alertas`. ⚠️ `null` = NO SE SABE, que no es «sin alertas». */
  alertas?: number | null;
  /** `alertas === 0`. */
  sinAlertas?: boolean;
  /** Suma capturada según el resumen guardado; `null` sin captura. */
  suma?: number | null;
  /** Contra la meta de tienda de HOY (sólo si está completa y sin IVA); `null` si no hay. */
  diferencia?: number | null;
  diferenciaPct?: number | null;
  /**
   * Propuestas ABIERTAS de condiciones y de suplentes de la tienda (fase 3). No
   * dependen de la semana: son del catálogo de la tienda.
   */
  propuestasAbiertas?: number;
  /**
   * Pendientes de catálogo que BLOQUEAN el envío y la autorización de la tienda
   * en esta semana (fase 4). Con alguno, `puedeAutorizarEnLote` es false y
   * `fueraDeLote` dice por qué.
   */
  pendientesBloqueantes?: number;
  // ---- fase B (21-oct-2026): el gerente y la revisión del supervisor.
  // Opcionales: una API anterior no los manda.
  /** Cuándo la mandó el gerente al supervisor (la última vez); `null` si nunca. */
  enviadoGerenteEn?: string | null;
  /** La persona elegida en «Cambiar usuario» al mandarla (informativo: no autentica). */
  enviadoGerenteNombre?: string | null;
  /** La nota del gerente para su supervisor. */
  notaGerente?: string | null;
  /** El supervisor se la devolvió al gerente y él todavía no la vuelve a mandar (es un borrador). */
  devuelta?: boolean;
  devueltaEn?: string | null;
  comentarioDevolucion?: string | null;
}

/** Cuántas tiendas hay en cada estado. Parcial: una API anterior no manda `revision`. */
export type Contadores = Partial<Record<EstadoTienda, number>>;

/** `GET semanas/:lunes/tiendas` */
export interface SemanaTiendas {
  semana: SemanaInfo;
  contadores: Contadores;
  /** De los borradores, cuántos son devoluciones al gerente sin atender (21-oct-2026). */
  devueltas?: number;
  tiendas: TiendaSemana[];
}

/**
 * `POST semanas/:lunes/autorizar-lote`. Cada tienda va en su propia
 * transacción y se juzga con lo que se lee DENTRO de ella:
 * - `omitidas`: al recalcular la revisión tiene alertas; se autoriza desde el detalle.
 * - `fallidas`: el mensaje que daría autorizarla sola (versión, estado, quién
 *   la envió, ventana, líneas pendientes, BUK caído…). Las demás siguen.
 */
export interface ResultadoLote {
  autorizadas: TiendaRef[];
  omitidas: (TiendaRef & { razon: string })[];
  fallidas: (TiendaRef & { mensaje: string })[];
}

/** `GET semanas/:lunes/contadores`: los números de las pestañas, sólo Postgres. */
export interface ContadoresPestanas {
  /** Enviadas que ESTA cuenta puede autorizar (0 fuera de la ventana; no cuenta lo que ella envió). */
  porAutorizar: number;
  /**
   * Lo que el gerente le mandó y espera su revisión: tiendas «con el
   * supervisor» que esta cuenta captura, dentro de su ventana (21-oct-2026).
   * Opcional: una API anterior no lo manda.
   */
  porRevisar?: number;
  /** Propuestas abiertas de condiciones y suplentes en sus tiendas, sin las que ella propuso. */
  propuestas: number;
  /**
   * Pendientes de catálogo ABIERTOS (de la última sincronización) que la cuenta
   * ve (fase 4). Opcional: una API anterior no lo manda.
   */
  pendientesCatalogo?: number;
}

// ------------------------------------------------------------ pendientes de catálogo (fase 4)

/** Los cinco tipos de la SPEC §5 (`TipoPendiente`), en el orden de las tarjetas del diseño. */
export type TipoPendiente =
  | 'COLABORADOR_SIN_PUESTO'
  | 'ALTA_SIN_CLAVE_VENDEDOR'
  | 'PUESTO_SIN_ESQUEMA_COMISION'
  | 'TIENDA_SIN_META_TIENDA'
  | 'COLABORADOR_EN_DOS_TIENDAS';

export const TIPOS_PENDIENTE: TipoPendiente[] = [
  'COLABORADOR_SIN_PUESTO',
  'ALTA_SIN_CLAVE_VENDEDOR',
  'PUESTO_SIN_ESQUEMA_COMISION',
  'TIENDA_SIN_META_TIENDA',
  'COLABORADOR_EN_DOS_TIENDAS',
];

/** Quién lo corrige en el sistema de origen (configurable en `configurations`). */
export type AreaPendiente = 'Recursos Humanos' | 'Comercial';

/** La acción de la fila: la ficha de la persona, los afectados de un puesto o la tienda. */
export type AccionPendiente = 'ver_ficha' | 'ver_afectados' | 'ver_tienda';

export interface TiendaDePendiente {
  chain: string;
  num: number;
  tienda: string;
}

/** Un afectado de «Puesto sin esquema de comisión» (`datos.personas`, ya limitado a las tiendas de la cuenta). */
export interface AfectadoPendiente {
  num: number | null;
  nombre: string;
  chain: string;
  tienda: number;
}

/** Un renglón de `GET /catalogo/pendientes`. Sólo lectura: se corrige en el sistema de origen. */
export interface PendienteCatalogo {
  id: number;
  tipo: TipoPendiente;
  tipoEtiqueta: string;
  accion: AccionPendiente;
  /** Lunes de la semana (tienda sin meta, en dos tiendas); `null` si no es de una semana. */
  semana: string | null;
  /** La tienda cuando es de UNA; `null` si es de varias o de toda la cadena. */
  tienda: TiendaDePendiente | null;
  tiendas: TiendaDePendiente[];
  cadena: string | null;
  /** Lo que va en la columna «Tienda» («VAZ 3 · SATÉLITE / VAZ 8 · TOREO», «VAZ · 12 tiendas»). */
  tiendaTexto: string;
  numeroColaborador: number | null;
  colaboradorKey: string | null;
  referencia: string;
  referenciaSub: string | null;
  /** «Qué bloquea». */
  detalle: string;
  area: AreaPendiente;
  /** Con la configuración de HOY: bloquea el envío y la autorización. */
  bloquea: boolean;
  abiertoEn: string;
  vistoEn: string;
  diasAbierto: number;
  /** «Abierto» en rojo: más de `diasAntiguedadRoja` días. */
  antiguo: boolean;
  resuelto: boolean;
  resueltoEn: string | null;
  /**
   * `afectados`: cuántos hay en la cadena. `personas`: los de las tiendas de la
   * cuenta (todos, para el admin), hasta 30. `personasTotal`: cuántos de la
   * cuenta antes de recortar. `cierre`: por qué se cerró
   * ('semana-fuera-de-ventana' = venció, nadie lo corrigió).
   */
  datos: {
    personas?: AfectadoPendiente[];
    afectados?: number;
    personasTotal?: number;
    cierre?: string;
    [k: string]: unknown;
  } | null;
}

export interface UltimaSincronizacion {
  iniciadoEn: string;
  terminadoEn: string | null;
  origen: 'cron' | 'manual';
  error: string | null;
  nuevos: number | null;
  resueltos: number | null;
  abiertos: number | null;
  notas: string[];
}

/** `GET /catalogo/pendientes?tipo&area&q&resueltos`. */
export interface ListaPendientesCatalogo {
  items: PendienteCatalogo[];
  conteos: {
    /** Abiertos con área y búsqueda aplicadas (sin el filtro de tipo). */
    total: number;
    /** Por tipo, SIN el filtro de tipo: las tarjetas son ese filtro. */
    porTipo: Record<TipoPendiente, number>;
    bloqueantes: number;
  };
  tipos: { tipo: TipoPendiente; etiqueta: string; area: AreaPendiente; bloquea: boolean }[];
  areas: AreaPendiente[];
  diasAntiguedadRoja: number;
  ultimaSincronizacion: UltimaSincronizacion | null;
  /** Hay una corrida en curso: «Actualizar» espera. */
  enCurso: boolean;
  /** Admin y regional pueden pedir una corrida a mano. */
  puedeSincronizar: boolean;
}

export interface FiltrosPendientes {
  tipo?: TipoPendiente | null;
  area?: AreaPendiente | null;
  q?: string;
  resueltos?: boolean;
}

/** `POST /catalogo/pendientes/sincronizar`. */
export interface ResumenSincronizacion {
  id: number;
  origen: 'cron' | 'manual';
  iniciadoEn: string;
  terminadoEn: string | null;
  nuevos: number;
  resueltos: number;
  siguen: number;
  abiertos: number;
  porTipo: Record<TipoPendiente, number>;
  /** Tipos que no se pudieron evaluar (una fuente caída): de ésos no se cerró nada. */
  sinEvaluar: TipoPendiente[];
  notas: string[];
  error: string | null;
}

/** Un pendiente de la tienda EN VIVO (vista, guardado y envío): banner y marca por fila. */
export interface PendienteDeTienda {
  tipo: TipoPendiente;
  etiqueta: string;
  bloquea: boolean;
  area: AreaPendiente;
  detalle: string;
  /** La fila a la que se refiere; `null` = es de la tienda. */
  colaboradorKey: string | null;
  referencia: string;
}

// ------------------------------------------------------------ una tienda

export interface Antiguedad {
  semana: number | null;
  etapa: Etapa | null;
  /**
   * Semanas que abarca la etapa con la regla de ESA semana (`hasta: null` = sin
   * fin). Sólo en inducción, garantía y vendedor; una API anterior no lo manda.
   * De aquí sale el texto, para no escribir «semanas 4 a 12» cuando ya no aplica.
   */
  semanasEtapa?: { desde: number; hasta: number | null };
}

export interface ApoyoVista {
  chain: string;
  num: number;
  dias: number;
  tienda: string | null;
}

export type OrigenFila = 'plantilla' | 'vendio_aqui' | 'manual';

export interface FilaVista {
  /** Llave que da el servidor. El navegador nunca la inventa. */
  key: string;
  numero: number | null;
  base: Base | null;
  agseq: string | null;
  agnum: string | null;
  nombre: string;
  puesto: string | null;
  puestoFuente: 'buk' | 'proscai' | null;
  fechaAlta: string | null;
  estadoBuk: string | null;
  antiguedad: Antiguedad;
  origen: OrigenFila;
  /** `false` = está capturada pero ya no sale en la plantilla de BUK de esta tienda. */
  enPlantilla: boolean;
  /** Tienda donde la misma persona ya tiene meta esta semana. */
  otraTienda: string | null;
  ventaRef4s: number | null;
  ventaRefSemanal: number | null;
  /** Venta semanal promedio de 8 semanas cerradas en todas sus tiendas; `null` = «—». */
  ventaPromedio?: number | null;
  ventaPromedioSemanas?: number | null;
  /** `meta / ventaPromedio − 1`; `null` sin meta o sin promedio. */
  vsPromedio?: number | null;
  /** Qué dice su puesto. Opcional sólo por tolerancia: sin él, `obligatoria`. */
  reglaMeta?: ReglaMeta;
  /** Esquema de comisión de su puesto (`vendedor`, `cajero`, `bodeguero`). */
  esquema?: string | null;
  metaSemanaAnterior: number | null;
  /**
   * El motivo que llevó la semana anterior (incluye los históricos, sólo para
   * mostrarlo) y su detalle. Se cruzan POR PERSONA, como la meta: un cambio de
   * llave entre semanas ya no los pierde. Opcionales: una API anterior no los manda.
   */
  motivoSemanaAnterior?: MotivoSinMeta | null;
  detalleSemanaAnterior?: string | null;
  metaMonto: number | null;
  sinMetaMotivo: MotivoSinMeta | null;
  sinMetaDetalle: string | null;
  apoyo: ApoyoVista | null;
  /**
   * Su programa de suplente ABIERTO (propuesto o autorizado), si tiene (fase 3).
   * ⚠️ Puede ser de OTRA tienda: la API lo busca por la persona, no por la
   * tienda, y no dice de cuál es. Opcional sólo por tolerancia: la API de
   * staging ya lo manda siempre.
   */
  suplente?: SuplenteFila | null;
  // ---- por persona desde el 21-oct-2026 (decisión del usuario: «Condiciones
  // de la tienda» salen de la captura). Opcionales sólo por tolerancia: sin
  // ellos (una API anterior) la pantalla no ofrece las casillas ni las manda.
  /** Apoyo de transporte. */
  transporte?: boolean;
  /** Bodeguero fijo con bodega de más de un piso. Sólo se marca si `esBodega`. */
  bodegueroFijo?: boolean;
  /**
   * Su puesto es de bodega: sólo así se le puede marcar «Bodeguero fijo». Y su
   * meta es opcional aunque su regla sea `no_lleva` (23-oct-2026).
   */
  esBodega?: boolean;
  /** Días de incapacidad de la semana (0 a 7). SÓLO informa: no cambia la meta. */
  diasIncapacidad?: number | null;
  /**
   * La meta que puso el gerente al enviarla al supervisor; `null` si la tienda
   * no pasó por el gerente, si está otra vez en sus manos (borrador) o si él no
   * le puso meta.
   */
  metaGerente?: number | null;
  /** El supervisor cambió la cifra del gerente (lo guardado): el regional ve «El gerente puso $X». */
  ajusteSupervisor?: boolean;
  /**
   * El renglón «Casa <tienda>» (23-oct-2026, ronda F, decisión del usuario): la
   * venta que se cobra con la cuenta «CASA…» del ERP cuando el único vendedor
   * descansa. Sólo lleva meta: cuenta para la meta de la tienda y nadie la
   * cobra. Llave `casa:<CADENA>:<num>`. Ausente en las demás filas.
   */
  esCasa?: boolean;
}

export interface EstadoVista {
  estado: EstadoTienda;
  version: number;
  notaSupervisor: string | null;
  enviadoPor: string | null;
  enviadoPorNombre?: string | null;
  enviadoEn: string | null;
  resueltoPor: string | null;
  resueltoPorNombre?: string | null;
  resueltoEn: string | null;
  comentario: string | null;
  /** Cuántas veces se envió: 2 = «2º envío». */
  numEnvio?: number;
  metaTiendaAlEnviar: number | null;
  sumaAlEnviar: number | null;
  actualizadoEn: string | null;
  // ---- fase B (21-oct-2026): el gerente y la revisión del supervisor.
  // Opcionales: una API anterior no los manda.
  /** Cuándo la mandó el gerente al supervisor (la última vez); `null` si nunca. */
  enviadoGerenteEn?: string | null;
  /** La cuenta de la tienda que la mandó (su correo). */
  enviadoGerenteCuenta?: string | null;
  /** La persona elegida en «Cambiar usuario» al mandarla (informativo: no autentica). */
  enviadoGerenteNombre?: string | null;
  /** La nota del gerente para su supervisor (la del supervisor al regional es `notaSupervisor`). */
  notaGerente?: string | null;
  /** La última devolución del supervisor, mientras el gerente no la vuelva a mandar. */
  devolucion?: { comentario: string | null; porNombre: string | null; en: string | null } | null;
}

/** `GET semanas/:lunes/tiendas/:chain/:num` (y lo que devuelven PUT y enviar). */
export interface VistaTienda {
  semana: SemanaInfo;
  tienda: { chain: string; num: number; nombre: string; supervisor: string; regional: string };
  estado: EstadoVista;
  metaTienda: MetaTiendaVista;
  /** `erp` = BUK no respondió y la plantilla salió de Proscai (no filtra bajas). */
  fuentePlantilla: 'buk' | 'erp';
  /** `false` = BUK no dio las fichas (puesto, alta, estado): se ve, pero no se guarda ni envía. */
  fichasBuk: boolean;
  referencia: { desde: string; hasta: string };
  colaboradores: FilaVista[];
  /**
   * Quienes vendieron en la tienda sin ser de su plantilla. La API lo sigue
   * mandando, pero desde el 21-oct-2026 la pantalla no lo muestra (decisión
   * del usuario): lo guardado con ese origen viene en `colaboradores`.
   */
  vendieronAqui: FilaVista[];
  totales: TotalesCaptura;
  validacion: { errores: Aviso[]; advertencias: Aviso[] };
  /** Los 3 chequeos del panel del regional. */
  revision?: RevisionAutomatica;
  /**
   * Condiciones de la tienda para la semana de la vista (fase 3).
   * @deprecated 21-oct-2026: la pantalla ya no las usa (Transporte y Bodeguero
   * van por persona, decisión del usuario); la API las sigue mandando.
   */
  condicion?: CondicionVista;
  permisos: {
    capturar: boolean;
    enviar: boolean;
    autorizar: boolean;
    rechazar: boolean;
    /** Sólo administradores, sobre una tienda autorizada. */
    reabrir?: boolean;
    /** El supervisor (o el admin) se la devuelve al gerente con un comentario: sólo «con el supervisor» (21-oct-2026). */
    devolver?: boolean;
    /** La cuenta de la tienda manda su borrador al supervisor (sólo en la app de la tienda). */
    enviarSupervisor?: boolean;
    /**
     * Puede proponer y certificar suplentes en la tienda (21-oct-2026): ya no
     * se deduce de `condicion.permisos.proponer`, que salió de la captura.
     * Opcional: una API anterior no lo manda.
     */
    suplentes?: boolean;
    /** Por qué no se puede capturar, en palabras, cuando la cuenta sí captura esa tienda. `null` si no aplica. */
    motivoNoCapturar: string | null;
    /**
     * Pendientes de catálogo que bloquean el envío y la autorización, en
     * palabras (fase 4); `null`/ausente = ninguno. Con alguno, `enviar` y
     * `autorizar` vienen en false.
     */
    bloqueoCatalogo?: string | null;
  };
  /**
   * Pendientes de catálogo de la tienda en la semana, EN VIVO (fase 4): banner
   * de la Captura, marca por fila (`colaboradorKey`) y alertas del regional.
   */
  pendientesCatalogo?: PendienteDeTienda[];
  /**
   * El renglón «Casa <tienda>» (ronda F, 23-oct-2026). Su presencia ES la
   * capacidad: una API anterior no lo manda y la pantalla no ofrece «+ Casa».
   * `puedeAgregar`/`puedeQuitar` ya traen el permiso de capturar (la cuenta de
   * la tienda no los tiene) y si la Casa está guardada (`enCaptura`).
   */
  casa?: CasaDeTienda;
  /** Lo que esta API sabe hacer y una anterior no (ronda F): sin él, no hay «partes iguales». */
  capacidades?: { repartirIguales?: boolean };
}

/** `VistaTienda.casa`. */
export interface CasaDeTienda {
  /** `casa:VAZ:57`. */
  key: string;
  /** «Casa REGINA». */
  nombre: string;
  enCaptura: boolean;
  puedeAgregar: boolean;
  puedeQuitar: boolean;
}

// ------------------------------------------------------------ copiar la semana anterior (fase 2b)

/** Por qué «Copiar semana anterior» no le propuso nada a una persona (`razon` de la API). */
export type RazonOmitida =
  | 'salio_de_plantilla'
  | 'solo_vendio_aqui'
  | 'nueva'
  | 'sin_captura_anterior'
  | 'meta_en_otra_tienda'
  | 'motivo_retirado'
  | 'puesto_sin_meta'
  | 'inactivo_en_buk'
  | 'no_valida';

export interface CopiaPropuesta {
  /** La llave de ESTA semana (la que va en el `PUT`). */
  key: string;
  nombre: string;
  /** Meta O motivo, nunca los dos. */
  metaMonto: number | null;
  sinMetaMotivo: MotivoSinMeta | null;
  sinMetaDetalle: string | null;
  /** Sólo con «Apoyo en otra tienda», que lo exige; el apoyo por días no se copia. */
  apoyo: ApoyoCaptura | null;
  /** La fila ya tiene meta o motivo guardados esta semana: copiar la pisaría. */
  tieneCaptura: boolean;
}

export interface CopiaOmitida {
  key: string;
  nombre: string;
  razon: RazonOmitida;
  /** La razón en palabras («ya tiene meta en TAF 118 · NORTE»). */
  mensaje: string;
}

/** `POST …/copiar-anterior`: una PROPUESTA; la API no guarda nada. */
export interface CopiaSemanaAnterior {
  semana: { lunes: string; numero: number };
  anterior: { lunes: string; numero: number; estado: EstadoTienda; filas: number };
  /** Versión de la tienda esta semana: si no es la de la pantalla, alguien guardó entre medias. */
  version: number;
  propuestas: CopiaPropuesta[];
  omitidas: CopiaOmitida[];
  /**
   * Transporte y Bodeguero fijo de la semana anterior, por persona (21-oct-2026).
   * Van aparte de `propuestas` porque también valen para quien no lleva meta.
   * Sólo las que difieren de esta semana. Los días de incapacidad no se copian.
   * Opcional: una API anterior no lo manda.
   */
  marcas?: CopiaMarcas[];
}

/** Las marcas por persona que «Copiar semana anterior» propone (ver `CopiaSemanaAnterior.marcas`). */
export interface CopiaMarcas {
  key: string;
  nombre: string;
  transporte: boolean;
  bodegueroFijo: boolean;
}

// ------------------------------------------------------------ peticiones

export interface ApoyoCaptura {
  chain: string;
  num: number;
  dias: number;
}

export interface CapturaFila {
  key: string;
  metaMonto: number | null;
  sinMetaMotivo: MotivoSinMeta | null;
  sinMetaDetalle?: string | null;
  apoyo?: ApoyoCaptura | null;
  /**
   * Por persona (21-oct-2026). Ausentes = la API conserva lo guardado: sólo se
   * mandan cuando la vista los trae (una API anterior respondería 400).
   */
  transporte?: boolean;
  bodegueroFijo?: boolean;
  /** 0 a 7; `null` = sin días. Sólo informa. */
  diasIncapacidad?: number | null;
}

export interface CapturaTienda {
  version: number;
  notaSupervisor?: string | null;
  filas: CapturaFila[];
  /**
   * Desde el 21-oct-2026 «Vendieron aquí» salió de la pantalla; desde el 23-oct
   * (ronda F) sólo lleva la llave de la Casa recién agregada (`casa:VAZ:57`),
   * y sólo si la vista anuncia la Casa (`vista.casa`: una API anterior no la conoce).
   */
  agregar?: string[];
  /** La Casa que se quitó (ronda F). Sólo la llave `casa:` de esa tienda; la API rechaza cualquier otra. */
  quitar?: string[];
}

export interface ResultadoResolver {
  estado: EstadoTienda;
  version: number;
}

/**
 * `GET semanas/:lunes/tiendas/:chain/:num/historial[?todo=true]`, lo más nuevo
 * primero. Por omisión sólo transiciones (enviar, autorizar, rechazar,
 * reabrir); con `todo`, también guardados, metas liberadas y la migración.
 * ⚠️ Nunca trae el correo: `nombre` es `users.name`, o el usuario del correo, o
 * «Migración» / «Sistema».
 */
export interface EventoHistorial {
  accion: string;
  nombre: string;
  /** Instante ISO. */
  fecha: string;
  estadoDesde: EstadoTienda | null;
  estadoHacia: EstadoTienda | null;
  comentario: string | null;
  /** Sólo en `enviar`: 1, 2… */
  numEnvio: number | null;
  /**
   * Sólo en `enviar_supervisor` (21-oct-2026): la persona que eligió «Cambiar
   * usuario» en el equipo de la tienda (informativo). La cuenta (`nombre`) es la
   * de la tienda.
   */
  persona?: string | null;
  /** Lo hizo la cuenta que mira (por id): el teléfono lo dice en segunda persona. */
  esMio?: boolean;
}

/** Cuerpo de un 409 cuando la misma persona ya tiene meta en otra tienda. */
export interface Duplicado {
  key: string;
  nombre: string;
  tienda: string;
}

// ------------------------------------------------------------ Excel (fase 2)

/** Una tienda, para pedir la plantilla de Excel (`tiendas=VAZ-1,TAF-22`). */
export interface TiendaRef {
  chain: string;
  num: number;
}

/** Un archivo bajado de la API, con el nombre ya armado en el navegador. */
export interface ArchivoDescargado {
  nombre: string;
  blob: Blob;
}

/** Una persona cuya captura cambia con el Excel, en palabras de la API. */
export interface CambioImportacion {
  nombre: string;
  antes: string;
  despues: string;
}

/** Una tienda del archivo: qué cambia, qué la bloquea y qué verá el regional. */
export interface TiendaImportacion {
  chain: string;
  num: number;
  /** `null` si el renglón trae una tienda que no está en el catálogo. */
  tienda: string | null;
  /** El estado ANTES de subir el archivo. */
  estado: EstadoTienda;
  cambios: CambioImportacion[];
  /** Con uno solo, la tienda NO se guarda (todo o nada por tienda). */
  errores: string[];
  advertencias: string[];
  guardada: boolean;
  /** Sólo al aplicar y si se guardó. */
  estadoNuevo: EstadoTienda | null;
}

/**
 * `POST semanas/:lunes/importar/validar` (`aplicado: false`, no guarda) y
 * `POST semanas/:lunes/importar` (`aplicado: true`), con la misma forma.
 */
export interface ResultadoImportacion {
  lunes: string;
  aplicado: boolean;
  tiendas: TiendaImportacion[];
  resumen: { tiendas: number; conErrores: number; cambios: number; guardadas: number };
}

// ------------------------------------------------------------ catálogo (fase 3)
/**
 * Condiciones de tienda y suplentes: los catálogos que pide el spec de
 * comisiones y que no existían en ningún sistema. El supervisor PROPONE, el
 * regional AUTORIZA. La vigencia la decide Postgres
 * (`metas_condiciones_vigentes` / `metas_suplentes_vigentes`), no la pantalla.
 *
 * ⚠️ Proponer otra vez RETIRA la propuesta abierta y crea otra con id NUEVO:
 * autorizar el id viejo da 409. Así un regional no autoriza lo que no vio.
 */
export type EstadoCatalogo = 'propuesta' | 'autorizada' | 'rechazada' | 'retirada';

/** Las tres casillas. Transporte = difícil acceso; Bodeguero = bodega de más de un piso Y bodeguero fijo. */
export interface CondicionesTienda {
  dificilAcceso: boolean;
  bodegaMasDeUnPiso: boolean;
  bodegueroFijo: boolean;
}

/** La versión AUTORIZADA que vale en una semana. */
export interface CondicionVigente extends CondicionesTienda {
  id: number;
  vigenteDesde: string;
}

export interface CondicionDetalle extends CondicionesTienda {
  id: number;
  chain: string;
  num: number;
  tienda: string | null;
  /** Lunes desde el que vale, `AAAA-MM-DD`. */
  vigenteDesde: string;
  estado: EstadoCatalogo;
  justificacion: string;
  /** Correo de quien propuso (su id si la cuenta no tiene correo). */
  propuestaPor: string;
  /** El nombre de la cuenta (con el correo de respaldo). Opcional: una API anterior no lo manda. */
  propuestaPorNombre?: string | null;
  propuestaEn: string;
  resueltoPor: string | null;
  resueltoPorNombre?: string | null;
  resueltoEn: string | null;
  comentario: string | null;
}

export interface PermisosCondicion {
  proponer: boolean;
  /** Puede resolver la propuesta: rechazarla siempre; autorizarla sólo si `autorizar`. */
  resolver: boolean;
  /** `false` también si la propuesta quedó antes de la ventana (la API daría 409): toca rechazarla. */
  autorizar: boolean;
  motivoNoAutorizar: string | null;
}

/** `VistaTienda.condicion`: la vigente en la semana de la vista y la propuesta abierta. */
export interface CondicionVista {
  vigente: CondicionVigente | null;
  /** La propuesta abierta de la tienda (hay a lo más una), sea de la semana que sea. */
  propuesta: CondicionDetalle | null;
  permisos: PermisosCondicion;
}

/** La última propuesta de condiciones que el regional RECHAZÓ, con lo que dijo. */
export interface CondicionRechazada {
  id: number;
  /** Lo que el regional pidió corregir (rechazar exige comentario). */
  comentario: string | null;
  resueltoPor: string | null;
  /** El nombre de quien la rechazó (con el correo de respaldo). Opcional: una API anterior no lo manda. */
  resueltoPorNombre?: string | null;
  resueltoEn: string | null;
  /** La justificación con que se había propuesto. */
  justificacion: string;
}

/**
 * `GET tiendas/:chain/:num/condiciones?lunes=` (y lo que devuelve proponer,
 * con `avisos`).
 *
 * ⚠️ Proponer responde con la vigente de la semana EN CURSO, no con la de la
 * semana que se está viendo: la pantalla conserva la suya.
 */
export interface CondicionesDeTienda {
  tienda: { chain: string; num: number; nombre: string; supervisor: string; regional: string };
  lunes: string;
  vigente: CondicionVigente | null;
  propuesta: CondicionDetalle | null;
  /** Últimas versiones AUTORIZADAS, la más reciente primero. */
  versiones: CondicionDetalle[];
  /** El lunes más antiguo que esta cuenta puede proponer; `null` = sin límite (admin). */
  vigenteDesdeMinimo: string | null;
  permisos: PermisosCondicion & { retirar: boolean };
  /**
   * La última propuesta, si el regional la RECHAZÓ después de la última
   * versión autorizada; `null` si no. Opcional: una API anterior no lo manda, y
   * sin él el panel simplemente no dice nada del rechazo.
   */
  ultimaRechazada?: CondicionRechazada | null;
  /** Sólo al proponer: p. ej. «Reemplaza la propuesta que había hecho …». */
  avisos?: string[];
}

export interface ProponerCondicionRequest extends CondicionesTienda {
  vigenteDesde: string;
  justificacion: string;
}

/** `FilaVista.suplente`: el programa abierto de la persona, con su vigencia en la semana de la vista. */
export interface SuplenteFila {
  id: number;
  estado: 'propuesta' | 'autorizada';
  inicioDesarrollo: string;
  certificadoEn: string | null;
  /** Lo decide `metas_suplentes_vigentes(lunes)` para la semana de la vista. */
  vigenteEstaSemana: boolean;
}

export interface SuplenteDetalle {
  id: number;
  chain: string;
  num: number;
  tienda: string | null;
  key: string;
  numero: number | null;
  base: Base | null;
  agseq: string | null;
  agnum: string | null;
  nombre: string;
  inicioDesarrollo: string;
  certificadoEn: string | null;
  estado: EstadoCatalogo;
  propuestaPor: string;
  /** El nombre de la cuenta (con el correo de respaldo). Opcional: una API anterior no lo manda. */
  propuestaPorNombre?: string | null;
  propuestaEn: string;
  resueltoPor: string | null;
  resueltoPorNombre?: string | null;
  resueltoEn: string | null;
  comentario: string | null;
}

export interface SuplenteAbierto extends SuplenteDetalle {
  /** ⚠️ De la semana EN CURSO (`lunes` de la respuesta), no de la que se ve. */
  vigenteEstaSemana: boolean;
  permisos: {
    resolver: boolean;
    autorizar: boolean;
    motivoNoAutorizar: string | null;
    retirar: boolean;
    certificar: boolean;
  };
}

/** `GET tiendas/:chain/:num/suplentes` (y lo que devuelve proponer, con `avisos`). */
export interface SuplentesDeTienda {
  tienda: { chain: string; num: number; nombre: string; supervisor: string; regional: string };
  /** La semana EN CURSO: con ella se calcula `vigenteEstaSemana`. */
  lunes: string;
  abiertos: SuplenteAbierto[];
  /** Los últimos rechazados o retirados, con el comentario del regional. */
  cerrados: SuplenteDetalle[];
  permisos: { proponer: boolean };
  /** Sólo al proponer: gerente o cajero, sin agente en el ERP, reemplazo. */
  avisos?: string[];
}

export interface ProponerSuplenteRequest {
  key: string;
  inicioDesarrollo: string;
  certificadoEn?: string | null;
  /** Lunes de la plantilla con que se eligió a la persona. */
  lunes: string;
}

/** Lo que `GET propuestas` agrega a cada propuesta. */
export interface PermisosPendiente {
  supervisor: string | null;
  regional: string | null;
  /** Rechazar. */
  puedeResolver: boolean;
  /** Autorizar: además, dentro de la ventana. */
  puedeAutorizar: boolean;
  motivoNoAutorizar: string | null;
  puedeRetirar: boolean;
}

export interface CondicionPendiente extends CondicionDetalle, PermisosPendiente {
  /** Lo que vale HOY en la tienda, para ver qué cambia. */
  vigenteActual: CondicionVigente | null;
}

export type SuplentePendiente = SuplenteDetalle & PermisosPendiente;

/**
 * `GET propuestas` (antes `GET pendientes`, que queda libre para la fase 4):
 * lo que espera resolución en las tiendas del alcance. Sólo Postgres.
 */
export interface PropuestasCatalogo {
  lunes: string;
  condiciones: CondicionPendiente[];
  suplentes: SuplentePendiente[];
}

// ------------------------------------------------------------ alcances (fase 5: venta real contra meta)

/**
 * Las bandas de alcance, de menor a mayor. Son los cortes del esquema de
 * comisiones (`parametros-comisiones.json`): por debajo de la base no se cobra,
 * de la base a 100 % la tasa base, de 100 % al acelerador la tasa alta, y del
 * acelerador en adelante el tramo máximo. La API manda los cortes vigentes.
 */
export type BandaAlcance = 'bajo' | 'medio' | 'alto' | 'max';

export interface TramoAlcance {
  banda: BandaAlcance;
  /** «Menos de 86 %», «86 a 99 %», «100 a 119 %», «120 % o más». */
  etiqueta: string;
  /** Límite inferior (incluido), como fracción; `null` = sin límite. */
  desde: number | null;
  /** Límite superior (EXCLUIDO); `null` = sin límite. */
  hasta: number | null;
}

/** Por qué una tienda no tiene alcance: `null` si lo tiene. */
export type MotivoTiendaSinAlcance = 'sin_meta_tienda' | 'meta_incompleta' | 'meta_con_iva' | null;

/** Lo que `GET alcances` y `GET alcances/tiendas/…` dicen de la semana. */
export interface SemanaDeAlcances {
  semana: { lunes: string; domingo: string; numero: number };
  /** El domingo ya pasó en hora de México. */
  cerrada: boolean;
  /** Los 7 días de venta están materializados y completos. */
  completo: boolean;
  /** Hora de México (`AAAA-MM-DDTHH:MM:SS`) de la materialización más vieja de la semana. */
  ventaAl: string | null;
  diasFaltantes: string[];
  /** Por qué no hay venta ni %, en palabras; `null` si la semana se puede medir. */
  aviso: string | null;
  /**
   * F5-01: la semana recién cerrada aún se puede autorizar o rechazar (hasta el
   * martes). Las cifras valen pero pueden cambiar; NO apaga las cifras, sólo lo dice.
   * Opcional: una API anterior no lo manda.
   */
  plazo?: string | null;
  /**
   * El Excel «para comisiones» (`export.xlsx`): deshabilitado con este porqué
   * mientras la semana se pueda autorizar o rechazar. Opcional: una API anterior no lo manda.
   */
  comisiones?: ComisionesDisponibilidad;
}

export interface TiendaConAlcance {
  chain: string;
  num: number;
  tienda: string;
  supervisor: string;
  regional: string;
  estado: EstadoTienda;
  /** Meta de tienda SIN IVA completa; `null` = sin meta utilizable. */
  meta: number | null;
  /** Lo que haya de meta aunque no sirva (incompleta o con IVA). */
  metaParcial: number | null;
  metaMotivo: MotivoTiendaSinAlcance;
  /** Venta neta SIN IVA de lunes a domingo; `null` si no se puede afirmar. */
  venta: number | null;
  /** venta ÷ meta (1 = 100 %), sin redondear. */
  pct: number | null;
  banda: BandaAlcance | null;
}

export interface KpisAlcances {
  alcanceRegional: { pct: number | null; venta: number; meta: number };
  tiendasEnMeta: { n: number; de: number; sinMeta: number };
  colaboradoresEnMeta: { n: number; de: number; sinClave: number };
  porBanda: { banda: BandaAlcance; etiqueta: string; n: number }[];
}

/** `GET alcances?semana&cadena&regional`: tiendas de mayor a menor alcance; las sin % al final. */
export interface AlcancesSemana extends SemanaDeAlcances {
  tramos: TramoAlcance[];
  kpis: KpisAlcances;
  tiendas: TiendaConAlcance[];
}

export interface ColaboradorConAlcance {
  key: string;
  nombre: string;
  puesto: string | null;
  numero: number | null;
  clave: string | null;
  /** Su meta AUTORIZADA de la semana (todas sus tiendas autorizadas); `null` = no tiene. */
  meta: number | null;
  /** Su meta en ESTA tienda, si está autorizada. */
  metaEnTienda: number | null;
  /** Su venta en TODAS las tiendas de su base. */
  venta: number | null;
  /** Su venta sólo en esta tienda. */
  ventaEnTienda: number | null;
  pct: number | null;
  banda: BandaAlcance | null;
  /** Por qué no hay %, en palabras; `null` si lo hay. */
  motivo: string | null;
  sinClave: boolean;
  /** El renglón de la Casa (ronda F): no es una persona y nadie la cobra; sin venta ni %. */
  esCasa?: boolean;
}

/** `GET alcances/tiendas/:chain/:num?semana=` (409 si la semana no ha cerrado). */
export interface AlcanceDeTienda extends SemanaDeAlcances, TiendaConAlcance {
  colaboradores: ColaboradorConAlcance[];
}

/** Filtros de la pestaña Alcances: sólo ACOTAN lo que la cuenta ve. */
export interface FiltrosAlcances {
  cadena?: string;
  regional?: string;
  /**
   * 21-oct-2026: el nombre del catálogo. La API lo valida contra el alcance de
   * la cuenta (400 si no es supervisor de ninguna de sus tiendas) y rehace los
   * indicadores con sus tiendas.
   */
  supervisor?: string;
}

// ------------------------------------------------------------ repartir según venta promedio (fase 6)

/**
 * Quien no recibe nada al repartir, y por qué. Los tres últimos sólo salen en
 * «partes iguales» (ronda F, 23-oct-2026): un admin anterior nunca los recibe.
 */
export type RazonOmitidaReparto =
  | 'puesto_sin_meta'
  | 'opcional_sin_meta'
  | 'con_motivo'
  | 'sin_meta'
  | 'fuera_de_plantilla'
  | 'sin_promedio'
  | 'meta_en_otra_tienda'
  | 'inactivo'
  | 'alta_posterior';

export interface OmitidaReparto {
  key: string;
  nombre: string;
  codigo: RazonOmitidaReparto;
  /** En palabras («su venta promedio es $0»). */
  razon: string;
}

/** Cómo se reparte lo que falta: según la venta promedio (fase 6) o en partes iguales (ronda F). */
export type ModoReparto = 'promedio' | 'iguales';

/** Lo que sale con la propuesta de una fila especial: «Se le está colocando meta al bodeguero / a la Casa». */
export type AvisoReparto = 'bodeguero' | 'casa';

export interface FilaPropuestaReparto {
  key: string;
  nombre: string;
  /** `null` sólo en «partes iguales»: ahí no hace falta promedio (un vendedor nuevo, la Casa). */
  ventaPromedio: number | null;
  /** `null` sólo en «partes iguales»: quien no tenía meta también recibe. */
  metaActual: number | null;
  metaPropuesta: number;
  /** Lo que le toca de lo que falta (nunca negativo). */
  diferencia: number;
  aviso?: AvisoReparto;
}

/**
 * `POST …/repartir`: una PROPUESTA; la API no guarda nada. Sólo las filas que
 * reciben algo van en `filas`; con la propuesta aplicada, la suma de TODAS las
 * metas de la tienda es `sumaPropuesta`, que es exactamente `metaTienda`.
 */
export interface RepartoPropuesta {
  /**
   * La API de la ronda F lo manda siempre; una anterior, nunca (ni aunque se le
   * pida «iguales»: no lee el cuerpo). Por eso «partes iguales» se reconoce por
   * `modo === 'iguales'`, no por haberlo pedido.
   */
  modo?: ModoReparto;
  /** «Partes iguales»: las llaves elegidas, en el orden de la captura. */
  elegidos?: string[];
  semana: { lunes: string; numero: number };
  /** Versión de la tienda: si no es la de la pantalla, alguien guardó entre medias. */
  version: number;
  metaTienda: number;
  sumaActual: number;
  porAsignar: number;
  sumaPropuesta: number;
  /** Múltiplo al que se redondeó la parte de cada fila ($100). */
  multiplo: number;
  filas: FilaPropuestaReparto[];
  omitidas: OmitidaReparto[];
}

/** Quién puede recibir en «partes iguales» (paso 1). */
export interface ElegibleReparto {
  key: string;
  nombre: string;
  puesto: string | null;
  /** `null` = sin meta (también puede recibir). */
  metaActual: number | null;
  ventaPromedio: number | null;
  /** La pantalla lo marca de entrada: vendedores y la Casa; gerente y bodeguero, sólo si ya tienen meta. */
  preseleccionado: boolean;
  aviso: 'bodeguero' | 'casa' | 'gerente' | null;
}

/**
 * `POST …/repartir` con `{ modo: 'iguales' }` y sin `keys` (paso 1, «elegir»):
 * a quién se le puede repartir y a quién no, con su razón. No guarda nada.
 */
export interface RepartoElegibles {
  modo: 'iguales';
  paso: 'elegir';
  semana: { lunes: string; numero: number };
  version: number;
  metaTienda: number;
  sumaActual: number;
  porAsignar: number;
  multiplo: number;
  /** En el orden de la captura. */
  elegibles: ElegibleReparto[];
  omitidas: OmitidaReparto[];
}

/** El cuerpo de `POST …/repartir`: sin él (o `promedio`) es «según venta promedio», como antes. */
export type CuerpoReparto = { modo: 'promedio' } | { modo: 'iguales'; keys?: string[] };

/** Lo que responde `POST …/repartir`: la propuesta o, en el paso 1 de «partes iguales», quién puede recibir. */
export type RespuestaReparto = RepartoPropuesta | RepartoElegibles;
